import {
  Paper,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Tooltip,
} from '@material-ui/core';
import { useStyles } from './customers-styles';
import { useEffect, useState } from 'react';
import { useAppSelector } from 'app';
import { AutoSyncSettingRequest, AutoSyncSettings } from 'components/setting/models/auto-sync-settings';
import CustomerSettingsApi from 'components/setting/api/customer-settings-api';
import AppSnackbar from 'components/snackbar/snackbar';
import { AutoSyncSettingsSource } from 'common/utils/shared/shared-constants';
import { DmsTypeEnum } from 'common/enums/DmsTypeEnum';
import { useSocialTestDealer } from 'common/context/SocialTestDealerContext';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const CustomerAutoSyncSettings: React.FC = () => {
  const classes: any = useStyles();
  const [showSnackBar] = AppSnackbar.useSnackBar();
  const [autoSyncSettings, setAutoSyncSettings] = useState<AutoSyncSettings[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  let dmsTypeId = useAppSelector((state) => state.user.dmsTypeId);
  const { isFacebookTestDealer, isInstagramTestDealer } = useSocialTestDealer();

  const fetchCustomersAutoSyncSettings = async () => {
    setLoading(true);
    try {
      const response = await CustomerSettingsApi.getCustomersAutoSyncSettings();
      setAutoSyncSettings(response);
    } catch (error) {
      showSnackBar({ type: 'error', message: 'Error fetching customers auto-sync settings.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomersAutoSyncSettings();
  }, []);

  const setCustomersAutoSyncSetting = async (sourceName: string, isAutoSyncEnabled: boolean) => {
    try {
      const settingReq: AutoSyncSettingRequest = {
        sourceName: sourceName,
        isAutoSyncEnabled: isAutoSyncEnabled,
      };
      const newAutoSyncSettings = await CustomerSettingsApi.setCustomersAutoSyncSetting(settingReq);
      setAutoSyncSettings(newAutoSyncSettings);

      showSnackBar({
        type: 'success',
        message: `Customer Sync settings updated successfully.`,
      });
    } catch (error) {
      showSnackBar({ type: 'error', message: 'Something went wrong.' });
    }
  };

  const handlerSwitchChange = async (sourceName: string, isAutoSyncEnabled: boolean) => {
    setLoading(true);
    await setCustomersAutoSyncSetting(sourceName, isAutoSyncEnabled);
    setLoading(false);
  };
 
  return (
<Paper className={classes.pageViewStyles}>
      <Grid container justifyContent="center" style={{ marginTop: '10px' }}>
        <Grid item xs={12} style={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Customer Sync
          </Typography>
        </Grid>
        <TableContainer className={classes.tableContainerAutoSync}>
          <Table stickyHeader id="auto-sync-settings-table">
            <TableHead>
              <TableRow>
                <TableCell>Sources</TableCell>
                <TableCell>Customer Sync</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Widget</TableCell>
                <TableCell>
                      <Switch
                        size="small"
                        disabled={loading}
                        checked={autoSyncSettings?.find((x) => x.sourceName == AutoSyncSettingsSource.Widget)?.isAutoSyncEnabled ?? false}
                        onChange={(event: any) => {
                          handlerSwitchChange(AutoSyncSettingsSource.Widget, event.target.checked);
                        }}
                      />
                </TableCell>
              </TableRow>
              {/* Hidding this Import option as again will be required in the future */}
              {/* <TableRow>
                <TableCell>Import</TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    disabled={loading}
                    checked={autoSyncSettings?.find((x) => x.sourceName == AutoSyncSettingsSource.Import)?.isAutoSyncEnabled ?? false}
                    onChange={(event: any) => {
                      handlerSwitchChange(AutoSyncSettingsSource.Import, event.target.checked);
                    }}
                  />
                </TableCell>
              </TableRow> */}
              <TableRow>
                <TableCell>Customers</TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    disabled={loading}
                    checked={autoSyncSettings?.find((x) => x.sourceName == AutoSyncSettingsSource.Customers)?.isAutoSyncEnabled ?? false}
                    onChange={(event: any) => {
                      handlerSwitchChange(AutoSyncSettingsSource.Customers, event.target.checked);
                    }}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Messenger</TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    disabled={loading}
                    checked={autoSyncSettings?.find((x) => x.sourceName == AutoSyncSettingsSource.Messenger)?.isAutoSyncEnabled ?? false}
                    onChange={(event: any) => {
                      handlerSwitchChange(AutoSyncSettingsSource.Messenger, event.target.checked);
                    }}
                  />
                </TableCell>
              </TableRow>
              {isFacebookTestDealer && (
              <TableRow>
                <TableCell>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Facebook
                    <Tooltip title={"Facebook source is disabled by default because Facebook leads can only be merged when they are not yet synced to the DMS."} placement="top">
                      <InfoOutlinedIcon fontSize="small" />
                    </Tooltip>
                  </span>
                </TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    disabled={loading}
                    checked={autoSyncSettings?.find((x) => x.sourceName == AutoSyncSettingsSource.Facebook)?.isAutoSyncEnabled ?? false}
                    onChange={(event: any) => {
                      handlerSwitchChange(AutoSyncSettingsSource.Facebook, event.target.checked);
                    }}
                  />
                </TableCell>
              </TableRow>
              )}
              {isInstagramTestDealer && (
               <TableRow>
                <TableCell>Instagram</TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    disabled={loading}
                    checked={autoSyncSettings?.find((x) => x.sourceName == AutoSyncSettingsSource.Instagram)?.isAutoSyncEnabled ?? false}
                    onChange={(event: any) => {
                      handlerSwitchChange(AutoSyncSettingsSource.Instagram, event.target.checked);
                    }}
                  />
                </TableCell>
              </TableRow>
              )}
              <TableRow>
                <TableCell>Deals</TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    disabled={loading}
                    checked={autoSyncSettings?.find((x) => x.sourceName == AutoSyncSettingsSource.Deals)?.isAutoSyncEnabled ?? false}
                    onChange={(event: any) => {
                      handlerSwitchChange(AutoSyncSettingsSource.Deals, event.target.checked);
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>   
</Paper> 
  );
};

export default CustomerAutoSyncSettings;
